import i18n from '@/plugins/i18n'
import { jsonEnglishLocalesContent } from '@/composables/useLocalesData'
import { SDS_CANONICAL_PREFIX, TDS_CANONICAL_PREFIX } from './useItemNamesFromLocalesFile'
import { IS_LANXESS } from '@/config/customers'
import { CanonicalAction, DataObjectData } from '@coac-gmbh/saifty-main-apis'
import {
  PICTOGRAMS_ICON_OPTIONS,
  SDS_CANONICAL_CREATED_BY,
  SDS_CANONICAL_DATE_OF_UPLOAD,
  SDS_CANONICAL_LAST_CHANGE_BY,
  SDS_CANONICAL_LAST_CHANGE_ON,
  SDS_CANONICAL_SOURCE,
  translateSECKeys,
  VALIDATION_LIST,
  isClassificationCanonical,
  getClassificationRegion,
  getClassificationCanonicalWithoutRegion,
} from '@/config'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { isClassificationItem } from './useApprovals'

dayjs.extend(customParseFormat)

export const APPROVAL_CANONICAL_ITEMS_FROM_ZERO_SECTION =
  window.ENV.APPROVALS_HEADER_CANONICALS?.split(',')
    .map((s) => s.trim())
    .filter((s) => s) || []
export const CANONICALS_TO_CREATE_FROM_ZERO_SECTION_IF_NO_EXISTS = [
  'SEC_0_1.RECEIVE_DATE',
  'SEC_0_1.VALIDITY_DATE_CONFIRMATION',
  'SEC_0_1.ADDITIONAL_INFORMATION',
  SDS_CANONICAL_SOURCE,
  'SEC_0_1.COMMENT',
  SDS_CANONICAL_LAST_CHANGE_ON,
  SDS_CANONICAL_LAST_CHANGE_BY,
  SDS_CANONICAL_DATE_OF_UPLOAD,
  SDS_CANONICAL_CREATED_BY,
  'SEC_0_1.DISTRIBUTION_TO_HSA',
  'SEC_0_1.UN_UA_NUMBER',
]
export const CANONICALS_TO_TRACK_CHANGES = [
  SDS_CANONICAL_LAST_CHANGE_ON,
  SDS_CANONICAL_LAST_CHANGE_BY,
  SDS_CANONICAL_DATE_OF_UPLOAD,
  SDS_CANONICAL_CREATED_BY,
].filter((canonical) => APPROVAL_CANONICAL_ITEMS_FROM_ZERO_SECTION.includes(canonical))

export type ComparisonOperator = '==' | '>' | '>=' | '<' | '<='
export type StringOperator = '==' | '!=' | 'includes'
export type ArrayOperator = 'includes'
export type DateValidationOperator = 'validFormat'
export type ItemExistenceOperator = 'exists'
export type ItemStatusOperator = 'is'

interface BaseRule<TType extends string, TOperator extends string> {
  type: TType
  testType: TOperator
}

export interface NumberMatchRule extends BaseRule<'number', ComparisonOperator> {
  valueToTest: number
}

export interface StringMatchRule extends BaseRule<'string', StringOperator>, CaseSensitivity {
  valueToTest: string
}

export interface ArrayMatchRule extends BaseRule<'array', ArrayOperator> {
  valueToTest: string[]
  validate?: (value: string, allowedValues: string[]) => boolean
}

export interface ItemStatusRule extends BaseRule<'itemStatus', ItemStatusOperator> {
  valueToTest: `${CanonicalAction}` // To generate the type from the enum and allow set a state like 'verified' as string in the validation list
}

export interface CanonicalMatchRule extends BaseRule<'canonical', ComparisonOperator | 'includes'>, CaseSensitivity {
  canonicalToTest: string
  valueType: 'date' | 'string' | 'number'
}

export type ItemExistsRule = BaseRule<'item', ItemExistenceOperator>

export type CurrentDateCompareRule = BaseRule<'currentDate', ComparisonOperator>

export interface CurrentDateFormatRule extends BaseRule<'currentDate', DateValidationOperator> {
  format: string
}

export type CurrentDateMatchRule = CurrentDateCompareRule | CurrentDateFormatRule

export type AnyMatchRule =
  | NumberMatchRule
  | StringMatchRule
  | ArrayMatchRule
  | CanonicalMatchRule
  | CurrentDateMatchRule
  | ItemExistsRule
  | ItemStatusRule

export interface ConditionalWithData {
  canonicalField: string
  matches: AnyMatchRule[]
  matchesRule: 'all' | 'any' | 'none'
  errorMessage?: string | ((value: string) => string)
  skipIfEmpty?: boolean
}

interface CaseSensitivity {
  caseSensitive?: boolean
}

export type CanonicalConditional = ConditionalWithData

function normalizeString(value: string, caseSensitive?: boolean): string {
  return caseSensitive ? value : value.toLowerCase()
}

function compareValues(a: number | string | Date, b: number | string | Date, operator: ComparisonOperator): boolean {
  let valueA = a
  let valueB = b
  if (valueA instanceof Date && valueB instanceof Date) {
    valueA = valueA.getTime()
    valueB = valueB.getTime()
  }

  switch (operator) {
    case '==':
      return valueA === valueB
    case '>':
      return valueA > valueB
    case '>=':
      return valueA >= valueB
    case '<':
      return valueA < valueB
    case '<=':
      return valueA <= valueB
    default:
      return false
  }
}

function evaluateRule(
  rule: AnyMatchRule,
  canonicalValue: string,
  canonicalMap: Map<string, string>,
  item?: DataObjectData,
): boolean {
  switch (rule.type) {
    case 'number':
      return compareValues(Number(canonicalValue), rule.valueToTest, rule.testType)

    case 'string': {
      const a = normalizeString(canonicalValue, rule.caseSensitive)
      const b = normalizeString(rule.valueToTest, rule.caseSensitive)

      switch (rule.testType) {
        case '!=':
          return a !== b
        case 'includes':
          return a.includes(b)
        default:
          return a === b
      }
    }

    case 'array':
      return rule.validate ? rule.validate(canonicalValue, rule.valueToTest) : rule.valueToTest.includes(canonicalValue)

    case 'canonical': {
      const otherValue = canonicalMap.get(rule.canonicalToTest)
      if (!otherValue) return true

      const parse = {
        date: (v: string) => dayjs(v, 'YYYY-MM-DD', true).valueOf(),
        number: Number,
        string: String,
      }[rule.valueType]

      const a = parse(canonicalValue)
      const b = parse(otherValue)

      if (rule.testType === 'includes') {
        const a = normalizeString(canonicalValue, rule.caseSensitive)
        const b = normalizeString(otherValue, rule.caseSensitive)

        if (rule.testType === 'includes') {
          return a.includes(b)
        }

        return compareValues(a, b, rule.testType)
      }

      return compareValues(a, b, rule.testType)
    }

    case 'currentDate': {
      const value = canonicalValue

      if (rule.testType === 'validFormat') {
        return dayjs(value, rule.format, true).isValid()
      }

      const parsed = dayjs(value, 'YYYY-MM-DD', true)
      if (!parsed.isValid()) return false

      const today = dayjs().startOf('day')
      return compareValues(parsed.startOf('day').valueOf(), today.valueOf(), rule.testType)
    }

    case 'item':
      return !!item

    case 'itemStatus': {
      if (!item?.action) return false

      const values = Array.isArray(rule.valueToTest) ? rule.valueToTest : [rule.valueToTest]

      return rule.testType === 'is' ? item.action === values[0] : values.includes(item.action)
    }
  }
}

export function evaluateApprovalCanonicalsConditions(
  data: DataObjectData[],
): { canonicalField: string; meetsCondition: boolean; errorMessage?: string }[] {
  const canonicalMap = new Map<string, string>(
    data
      .filter(
        (item): item is DataObjectData => typeof item.fieldName === 'string' && typeof item.fieldValue === 'string',
      )
      .map((item) => [item.fieldName, item.fieldValue]),
  )

  return VALIDATION_LIST.canonicalsValidations.map((conditional) => {
    const result = {
      canonicalField: conditional.canonicalField,
      meetsCondition: false,
      errorMessage: translateSECKeys(
        typeof conditional.errorMessage === 'function'
          ? conditional.errorMessage(canonicalMap.get(conditional.canonicalField) || '')
          : conditional.errorMessage || '',
      ),
    }

    const item = data.find((d) => d.fieldName === conditional.canonicalField)
    const canonicalValue = canonicalMap.get(conditional.canonicalField)

    if ((canonicalValue === undefined || canonicalValue === null || canonicalValue === '') && conditional.skipIfEmpty) {
      return { ...result, meetsCondition: true }
    }

    const evaluations = conditional.matches.map((rule) => evaluateRule(rule, canonicalValue || '', canonicalMap, item))

    switch (conditional.matchesRule) {
      case 'all':
        result.meetsCondition = evaluations.every(Boolean)
        break
      case 'any':
        result.meetsCondition = evaluations.some(Boolean)
        break
      case 'none':
        result.meetsCondition = evaluations.every((v) => !v)
        break
    }

    return result
  })
}

export function canTheItemBeDeleted(canonicalField?: string): boolean {
  if (!canonicalField) {
    return true
  }

  return !VALIDATION_LIST.canonicalsValidations.some((conditional) => {
    // We are only interested in validations of the same canonical
    if (conditional.canonicalField !== canonicalField) {
      return false
    }

    // If there are no matches, we cannot validate anything
    if (!('matches' in conditional)) {
      return false
    }

    // We search for a rule that requires the item to exist
    return conditional.matches.some((rule) => rule.type === 'item' && rule.testType === 'exists')
  })
}

export function getCanonicalNameWithoutNumberSuffix(canonical: string) {
  let valueToReturn = canonical

  // Note: Remember that the title convention is SEC_<number>.<title>
  const [itemSection = '', itemTitle = ''] = canonical.split('.', 2)
  if (itemTitle) {
    const titleSplit = itemTitle.split('_')
    const numberOfTheItem = parseInt(titleSplit[titleSplit.length - 1])
    const titleWithoutLastPart = titleSplit.slice(0, -1)

    if (typeof numberOfTheItem == 'number' && !isNaN(numberOfTheItem)) {
      // We join the item section and the title without the index to obtains the translation, because does not exist a translation with index number
      valueToReturn = itemSection + '.' + titleWithoutLastPart.join('_')
    }
  }

  return valueToReturn
}

export function getCanonicalNumberSuffix(canonical: string) {
  let valueToReturn = undefined

  try {
    if (canonical) {
      // Note: Remember that the title convention is SEC_<number>.<title>
      const canonicalSplit = canonical.split('.')

      const itemTitle = canonicalSplit[1]
      if (itemTitle) {
        const titleSplit = itemTitle.split('_')
        const numberOfTheItem = parseInt(titleSplit[titleSplit.length - 1])
        valueToReturn = numberOfTheItem
      }
    }
  } catch (error) {
    console.log(`Canonical: ${canonical} - Error: ${error}`)
  }

  return valueToReturn
}

export function getItemTitleTranslation(
  itemSectionAndTitle: string,
  isCustomPassportItem?: boolean,
  instanceNumber?: number,
) {
  // eslint-disable-next-line
  // @ts-ignore
  let titleToReturn = i18n.global.t(itemSectionAndTitle)
  const itemIsPresentInLocalesFile =
    !!(itemSectionAndTitle in jsonEnglishLocalesContent) && !!jsonEnglishLocalesContent[itemSectionAndTitle]

  // Note: Remember that the title convention is SEC_<number>.<title>
  const [itemSection = '', itemTitle = ''] = itemSectionAndTitle.split('.', 2)

  if (
    !itemIsPresentInLocalesFile &&
    itemTitle &&
    (itemSectionAndTitle.startsWith(SDS_CANONICAL_PREFIX) || itemSectionAndTitle.startsWith(TDS_CANONICAL_PREFIX))
  ) {
    // We only translate the items with the numeric structure when we know the structure (SDS and TDS cases), but if the element has another structure ending with a number, it is possible that the user is using a custom passport, and wants an exact translation of the element or the canonical name of the element as fallback
    // You can see at the 'GGNCustomTranslations.json' file to check that in the file there are items ending with a number and we want to translate them literally as we receive them
    const titleSplit = itemTitle.split('_')
    const numberOfTheItem = parseInt(titleSplit[titleSplit.length - 1])
    const titleWithoutLastPart = titleSplit.slice(0, -1)

    if (typeof numberOfTheItem == 'number' && !isNaN(numberOfTheItem)) {
      // We join the item section and the title without the index to obtains the translation, because does not exist a translation with index number
      // eslint-disable-next-line
      // @ts-ignore
      const titleTranslation = i18n.global.t(itemSection + '.' + titleWithoutLastPart.join('_'))
      const titleIndex = numberOfTheItem + 1 // We add one, because we do not want to start by 0
      let joinString = ' '
      const isClassification = isClassificationItem(itemSectionAndTitle)
      // To only add the instance word to the classification items
      if (isClassification) {
        joinString = ` - ${i18n.global.t('Instance')} `
      }
      titleToReturn = titleTranslation + joinString + (isClassification ? instanceNumber : titleIndex)
    }
    if (IS_LANXESS) {
      if (itemSectionAndTitle === 'SEC_9_2.SURFACE_TENSION') {
        titleToReturn = 'Surface tension'
      }
      if (itemSectionAndTitle === 'SEC_11_1.CELL_MUTAGEN') {
        titleToReturn = 'Genotoxicity in vivo'
      }
    }
  } else if (itemTitle && isCustomPassportItem && itemSectionAndTitle.startsWith('REGULATION_')) {
    // titleToReturn = titleToReturn // For this case, we want to return the same string, but typescript throw error due to the string references to itself
  } else if (isCustomPassportItem) {
    const tryTranslationLocaleTranslation = i18n.global.t(itemSectionAndTitle)
    titleToReturn =
      tryTranslationLocaleTranslation !== itemSectionAndTitle
        ? tryTranslationLocaleTranslation
        : i18n.global.t(itemTitle)
  }

  return titleToReturn
}

// Translates a canonical that may belong to a classification region (e.g. SEC_2_1.GB_CLASSIFICATION_HAZARDCLASS_0),
// normalizing the region segment before looking up the translation and prefixing it to the label
export function getItemTitleTranslationWithRegion(
  itemSectionAndTitle: string,
  isCustomPassportItem?: boolean,
  instanceNumber?: number,
) {
  if (isClassificationCanonical(itemSectionAndTitle)) {
    const region = getClassificationRegion(itemSectionAndTitle)
    const base = getItemTitleTranslation(
      getClassificationCanonicalWithoutRegion(itemSectionAndTitle),
      isCustomPassportItem,
      instanceNumber,
    )
    return region ? `${region} ${base}` : base
  }
  return getItemTitleTranslation(itemSectionAndTitle, isCustomPassportItem, instanceNumber)
}

export default function useValidationItem() {
  function getPictogramsByValue(pictogramId: string) {
    return PICTOGRAMS_ICON_OPTIONS.filter((pictogram) => pictogramId.toUpperCase().includes(pictogram.id)) || []
  }

  function getNameOfTheCustomItem(itemSectionAndTitle: string) {
    let titleToReturn = itemSectionAndTitle
    const canonicalParts = itemSectionAndTitle.split('.')
    if (canonicalParts.length > 0 && canonicalParts.length <= 2) {
      titleToReturn = canonicalParts[1] // We use the name after of the point of the canonical name as name
    }

    return titleToReturn
  }

  return { getPictogramsByValue, getItemTitleTranslation, getItemTitleTranslationWithRegion, getNameOfTheCustomItem }
}
